<template>
  <v-container class="calendar-container">
    <!-- Kalender-Hauptkarte mit Glasmorphismus-Effekt -->
    <v-card class="status-overview-card mb-6">
      <!-- Tab-Navigation für Terminkalender/Urlaubsplaner -->
      <v-tabs
        v-model="activeTab"
        bg-color="transparent"
        color="success"
        class="calendar-tabs"
      >
        <v-tab value="termine">
          <v-icon start>mdi-calendar</v-icon>
          Terminkalender
        </v-tab>
        <v-tab value="urlaub">
          <v-icon start>mdi-beach</v-icon>
          Urlaubsplaner
        </v-tab>
      </v-tabs>

      <v-divider class="card-divider"></v-divider>

      <!-- Kalender-Header mit verbesserten Navigationselementen -->
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">
          {{ activeTab === 'termine' ? 'mdi-calendar' : 'mdi-beach' }}
        </v-icon>
        {{ activeTab === 'termine' ? 'Terminkalender' : 'Urlaubsplaner' }}
        <v-spacer></v-spacer>

        <!-- Toggle für Eingabemodus -->
        <div class="month-navigation">
          <v-btn
            variant="text"
            density="comfortable"
            color="success"
            class="nav-btn prev-btn"
            @click="prevMonth"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <h2 class="month-title">
            {{ monthLabels[selectedMonth - 1] }} {{ selectedYear }}
          </h2>

          <v-btn
            variant="text"
            density="comfortable"
            color="success"
            class="nav-btn next-btn"
            @click="nextMonth"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <v-card-text class="calendar-actions-container">
        <div class="calendar-actions">
          <v-btn
            variant="outlined"
            size="small"
            color="success"
            class="today-btn"
            @click="goToToday"
          >
            <v-icon start size="small">mdi-calendar-today</v-icon>
            Heute
          </v-btn>

          <!-- Dropdown für Monat/Jahr -->
          <v-menu
            location="bottom right"
            transition="scale-transition"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                variant="outlined"
                color="success"
                size="small"
                v-bind="props"
                class="date-selector"
              >
                <v-icon start size="small">mdi-calendar-month</v-icon>
                Monat/Jahr wählen
                <v-icon end size="small">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-card class="dialog-card date-picker-card">
              <v-card-text>
                <div class="date-picker-container">
                  <v-select
                    v-model="selectedMonth"
                    :items="monthItems"
                    item-title="text"
                    item-value="value"
                    label="Monat"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    class="month-select form-field"
                    color="success"
                    bg-color="rgba(30, 30, 40, 0.5)"
                  ></v-select>

                  <v-select
                    v-model="selectedYear"
                    :items="yearItems"
                    label="Jahr"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    class="year-select form-field"
                    color="success"
                    bg-color="rgba(30, 30, 40, 0.5)"
                  ></v-select>
                </div>
              </v-card-text>
            </v-card>
          </v-menu>

          <v-btn
            variant="outlined"
            size="small"
            color="success"
            class="today-btn"
            @click="openAddEventDialog"
          >
            <v-icon start>mdi-plus</v-icon>
            Termin
          </v-btn>
        </div>
      </v-card-text>

      <!-- Tab-Inhalte -->
      <div v-if="activeTab === 'termine'">
        <!-- Desktop-Kalender-Ansicht -->
        <div v-if="!isMobile" class="calendar-container desktop-calendar">
        <!-- Wochentage-Header -->
        <div class="week-days-header">
          <div v-for="day in weekDays" :key="day" class="week-day-label">
            {{ day }}
          </div>
        </div>

        <!-- Kalender-Raster -->
        <div class="calendar-grid">
          <div
            v-for="(week, weekIndex) in weeks"
            :key="weekIndex"
            class="calendar-week"
          >
            <div
              v-for="(day, dayIndex) in week"
              :key="dayIndex"
              class="calendar-day"
              :class="{
                today: isToday(day.date),
                'non-current-month': !day.inCurrentMonth,
                weekend: isDayWeekend(day.date),
                'has-events': getDraggableEvents(day.date).length > 0,
              }"
              @dblclick="openAddEventDialog(day.date)"
            >
              <!-- Kalenderwoche -->
              <span v-if="dayIndex === 0" class="week-number">
                {{ getWeekNumber(day.date) }}
              </span>

              <!-- Datumsanzeige mit Punkt für aktuelle Tage -->
              <div class="day-number">
                <div
                  class="date-container"
                  :class="{ 'today-marker': isToday(day.date) }"
                >
                  {{ day.date.getTime() !== 0 ? day.date.getDate() : "" }}
                </div>
              </div>

              <!-- Events-Container mit Drag & Drop -->
              <draggable
  v-if="day.date.getTime() !== 0"
  :list="getDraggableEvents(day.date)"
  group="events"
  itemKey="id"
  @change="onEventChanged(day.date, $event)"
  class="day-events"
>
  <template #item="{ element: event }">
    <div
      class="event-item"
      :class="{
        'holiday-event': isEventType(event, 'holiday'),
        'ferien-event': isEventType(event, 'ferien'),
        'user-event':
          !isEventType(event, 'holiday') &&
          !isEventType(event, 'ferien'),
        'owned-event': event.user_id === authStore.user?.id,
        'vacation-event': event.event_type === 'vacation',
        'sick-event': event.event_type === 'sick',
        'birthday-event': event.event_type === 'birthday',
      }"
      :style="{
        borderLeftColor: event.color,
        backgroundColor: getEventBackground(event),
      }"
      @dblclick="
        authStore.user &&
          event.user_id === authStore.user.id &&
          openEditEventDialog(event)
      "
    >
      <!-- Event-Content mit Tooltip -->
      <v-tooltip
        location="bottom"
        :disabled="false"
      >
        <template #activator="{ props }">
          <div class="event-content" v-bind="props">
            <!-- Event-Icon für Event-Typ -->
            <v-icon v-if="event.event_type === 'birthday'" class="event-icon" size="x-small" color="#E91E63">
              mdi-cake-variant
            </v-icon>
            <v-icon v-else-if="event.event_type === 'vacation'" class="event-icon" size="x-small" color="#FF9800">
              mdi-beach
            </v-icon>
            <v-icon v-else-if="event.event_type === 'sick'" class="event-icon" size="x-small" color="#F44336">
              mdi-medical-bag
            </v-icon>

            <div class="event-time">
              {{ formatTime(event.start_date) }}
            </div>
            <div class="event-title">
              {{ event.title }}
              <!-- Mitarbeiternummer Badge -->
              <span v-if="shouldShowMitarbeiternummer(event)" class="user-badge">
                {{ formatMitarbeiternummer(event.mitarbeiternummer) }}
              </span>
            </div>
          </div>
        </template>
        <div class="event-tooltip-content">
          <div v-if="event.description && event.description.trim() !== ''">
            <strong>{{ event.title }}</strong><br>
            {{ event.description }}
          </div>
          <div v-else>
            <strong>{{ event.title }}</strong>
          </div>
          <!-- Besitzername anzeigen -->
          <div v-if="shouldShowCreatorInfo(event)" class="tooltip-creator-info">
            <v-icon size="x-small">mdi-account</v-icon>
            {{ getEventCreatorInfo(event) }}
          </div>
        </div>
      </v-tooltip>

      <!-- Edit-Button für eigene Events -->
      <v-btn
        v-if="
          authStore.user && event.user_id === authStore.user.id
        "
        icon
        x-small
        class="event-edit-btn"
        @click.stop="openEditEventDialog(event)"
      >
        <v-icon size="small">mdi-pencil</v-icon>
      </v-btn>
    </div>
  </template>
</draggable>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile-Agenda-Ansicht -->
      <div v-else class="calendar-container mobile-calendar">
        <div class="mobile-day-list">
          <div
            v-for="day in allDaysInMonth"
            :key="day.date"
            class="mobile-day-item"
            :class="{
              today: isToday(day.date),
              weekend: isDayWeekend(day.date),
              'has-events': eventsOnDate(day.date).length > 0,
            }"
          >
            <div class="mobile-day-header">
              <div class="mobile-date-info">
                <span class="mobile-day-name">{{ getDayName(day.date) }}</span>
                <span class="mobile-day-number">{{ day.date.getDate() }}</span>
              </div>
              <span class="mobile-week-number">
                {{ getWeekNumber(day.date) }}
              </span>
            </div>

            <div class="mobile-day-events">
              <div
                v-for="event in eventsOnDate(day.date)"
                :key="event.id"
                class="mobile-event-item"
                :class="{
                  'holiday-event': isEventType(event, 'holiday'),
                  'ferien-event': isEventType(event, 'ferien'),
                  'user-event':
                    !isEventType(event, 'holiday') &&
                    !isEventType(event, 'ferien'),
                  'owned-event': event.user_id === authStore.user?.id,
                  'vacation-event': event.event_type === 'vacation',
                  'sick-event': event.event_type === 'sick',
                  'birthday-event': event.event_type === 'birthday',
                }"
                :style="{
                  borderLeftColor: event.color,
                  backgroundColor: getEventBackground(event),
                }"
                @click="
                  authStore.user &&
                    event.user_id === authStore.user.id &&
                    openEditEventDialog(event)
                "
              >
                <div class="mobile-event-content">
                  <div class="mobile-event-time">
                    {{ formatTime(event.start_date) }}
                  </div>
                  <div class="mobile-event-title">{{ event.title }}</div>
                </div>

                <v-btn
                  v-if="authStore.user && event.user_id === authStore.user.id"
                  icon
                  x-small
                  class="mobile-event-edit-btn"
                  @click.stop="openEditEventDialog(event)"
                >
                  <v-icon size="small">mdi-pencil</v-icon>
                </v-btn>
              </div>

              <!-- Leere Nachricht, wenn keine Events vorhanden sind -->
              <div
                v-if="eventsOnDate(day.date).length === 0"
                class="no-events-message"
              >
                Keine Termine an diesem Tag
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <!-- Urlaubsplaner Tab-Inhalt -->
      <div v-else-if="activeTab === 'urlaub'">
        <UrlaubsplanerCalendar
          :selected-month="selectedMonth"
          :selected-year="selectedYear"
          :weeks="weeks"
          :week-days="weekDays"
        />
      </div>
    </v-card>

    <!-- Dialog für Event hinzufügen/bearbeiten -->
    <v-dialog v-model="eventDialog" max-width="500px" class="event-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon
            :color="getEventTypeColor(eventItem.event_type)"
            size="24"
            class="mr-2"
          >
            {{ getEventTypeIcon(eventItem.event_type) }}
          </v-icon>
          <span>{{
            eventEditMode ? "Termin bearbeiten" : "Neuer Termin"
          }}</span>

          <v-spacer></v-spacer>

          <v-btn
            v-if="eventEditMode"
            icon
            variant="text"
            color="error"
            class="delete-btn"
            @click="confirmDelete = true"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <!-- Neues Auswahlfeld für Eventtyp -->
          <v-select
            v-model="eventItem.event_type"
            :items="eventTypes"
            item-title="text"
            item-value="value"
            label="Typ"
            variant="outlined"
            density="comfortable"
            class="mb-3 form-field"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="success"
            @update:model-value="onEventTypeChange"
          >
            <template v-slot:prepend-inner>
              <v-icon :color="getEventTypeColor(eventItem.event_type)">{{
                getEventTypeIcon(eventItem.event_type)
              }}</v-icon>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-icon :color="getEventTypeColor(item.raw.value)">{{
                    getEventTypeIcon(item.raw.value)
                  }}</v-icon>
                </template>
                <v-list-item-title>{{ item.raw.text }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-select>

          <v-text-field
            v-model="eventItem.title"
            :label="
              eventItem.event_type === 'appointment'
                ? 'Titel *'
                : 'Beschreibung (optional)'
            "
            variant="outlined"
            density="comfortable"
            class="mb-3 form-field"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="success"
            :required="eventItem.event_type === 'appointment'"
            :rules="
              eventItem.event_type === 'appointment'
                ? [(v) => !!v || 'Titel wird benötigt']
                : []
            "
            :placeholder="getEventTypePlaceholder(eventItem.event_type)"
          >
            <template v-slot:prepend-inner>
              <v-icon color="success">{{
                eventItem.event_type === "appointment"
                  ? "mdi-format-title"
                  : "mdi-text-box-outline"
              }}</v-icon>
            </template>
          </v-text-field>

          <v-textarea
            v-if="eventItem.event_type === 'appointment'"
            v-model="eventItem.description"
            label="Beschreibung"
            variant="outlined"
            rows="3"
            auto-grow
            placeholder="Beschreibung (optional)"
            class="mb-3 form-field"
            bg-color="rgba(30, 30, 40, 0.5)"
            color="success"
          >
            <template v-slot:prepend-inner>
              <v-icon color="success">mdi-text-box-outline</v-icon>
            </template>
          </v-textarea>

          <div class="date-time-fields-wrapper">
            <div class="date-field">
              <label class="field-label">Beginn:</label>
              <v-text-field
                v-model="eventItem.start_date"
                type="datetime-local"
                variant="outlined"
                density="compact"
                class="full-width-datetime form-field"
                bg-color="rgba(30, 30, 40, 0.5)"
                color="success"
                required
                @blur="validateDateTimeField('start_date')"
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-start</v-icon>
                </template>
              </v-text-field>
            </div>

            <div class="date-field">
              <label class="field-label">Ende:</label>
              <v-text-field
                v-model="eventItem.end_date"
                type="datetime-local"
                variant="outlined"
                density="compact"
                class="full-width-datetime form-field"
                bg-color="rgba(30, 30, 40, 0.5)"
                color="success"
                required
                @blur="validateDateTimeField('end_date')"
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-end</v-icon>
                </template>
              </v-text-field>
            </div>
          </div>

          <v-checkbox
            v-model="eventItem.is_global"
            :label="getGlobalCheckboxLabel(eventItem.event_type)"
            color="success"
            hide-details
            class="mb-4"
          >
            <template v-slot:prepend>
              <v-icon color="success">mdi-eye-outline</v-icon>
            </template>
          </v-checkbox>

          <!-- Farbauswahl nur für reguläre Termine -->
          <div
            v-if="eventItem.event_type === 'appointment'"
            class="color-selection-container"
          >
            <div class="color-selection-label">
              <v-icon color="success" class="mr-2">mdi-palette</v-icon>
              Farbe auswählen
            </div>
            <div class="color-options">
              <div
                v-for="color in eventColors"
                :key="color.value"
                class="color-option"
                :style="{ backgroundColor: color.value }"
                :class="{ selected: eventItem.color === color.value }"
                @click="eventItem.color = color.value"
              ></div>
            </div>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="eventDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            variant="elevated"
            :disabled="!isEventValid"
            @click="saveEvent"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialog für Löschung -->
    <v-dialog v-model="confirmDelete" max-width="400px" class="confirm-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title warning-header d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Termin löschen?
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content text-center pa-6">
          <p>
            Möchten Sie den Termin "<strong>{{ eventItem.title }}</strong
            >" wirklich löschen?
          </p>
          <p class="text-caption mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="confirmDelete = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="elevated" @click="deleteEventConfirmed">
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
  nextTick,
  provide,
} from "vue";
import axios from "axios";
import draggable from "vuedraggable";
import Holidays from "date-holidays";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";
import UrlaubsplanerCalendar from "@/components/UrlaubsplanerCalendar.vue";

// ======== Zustandsverwaltung ========
const toast = useToast();
const authStore = useAuthStore();

// Tab-Zustand
const activeTab = ref('termine'); // 'termine' oder 'urlaub'

// Kalender-Zustand
const selectedMonth = ref(new Date().getMonth() + 1); // 1-12
const selectedYear = ref(new Date().getFullYear());
const events = ref([]);
const eventDialog = ref(false);
const eventEditMode = ref(false);
const confirmDelete = ref(false);
const loading = ref(false);

// Responsive Design
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 960);

// ======== Konstanten & Konfiguration ========
const monthLabels = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const weekDays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// Farboptionen für Benutzerevents
const eventColors = [
  { text: "Blau", value: "#1976D2" },
  { text: "Grün", value: "#4CAF50" },
  { text: "Rot", value: "#F44336" },
  { text: "Orange", value: "#FF9800" },
  { text: "Lila", value: "#9C27B0" },
  { text: "Türkis", value: "#009688" },
  { text: "Rosa", value: "#E91E63" },
  { text: "Dunkelblau", value: "#3F51B5" },
];

// ======== Berechnete Eigenschaften ========
const monthItems = computed(() => {
  return monthLabels.map((label, index) => ({
    text: label,
    value: index + 1,
  }));
});

const yearItems = computed(() => {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 10; y <= currentYear + 10; y++) {
    years.push(y);
  }
  return years;
});

/**
 * Berechnet die Wochen für den aktuellen Monat
 * Jede Woche ist ein Array von Tagen mit Datum und Info, ob der Tag zum aktuellen Monat gehört
 */
const weeks = computed(() => {
  const weeksArray = [];
  const year = selectedYear.value;
  const month = selectedMonth.value; // 1-12
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);

  // Berechne Offset: Anzahl der Tage des Vormonats, die in der ersten Woche erscheinen
  const offset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

  const days = [];

  // Vormonat: Füge die letzten "offset" Tage hinzu
  const prevMonth = month - 1 === 0 ? 12 : month - 1;
  const prevMonthYear = month - 1 === 0 ? year - 1 : year;
  const lastDatePrevMonth = new Date(prevMonthYear, prevMonth, 0).getDate();
  for (let i = offset; i > 0; i--) {
    const date = new Date(
      prevMonthYear,
      prevMonth - 1,
      lastDatePrevMonth - i + 1
    );
    days.push({ date, inCurrentMonth: false });
  }

  // Aktueller Monat: Alle Tage hinzufügen
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month - 1, d);
    days.push({ date, inCurrentMonth: true });
  }

  // Folgemonat: Fülle den Rest einer vollen Woche auf
  const remaining = days.length % 7;
  if (remaining !== 0) {
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextMonthYear = month === 12 ? year + 1 : year;
    const count = 7 - remaining;
    for (let d = 1; d <= count; d++) {
      const date = new Date(nextMonthYear, nextMonth - 1, d);
      days.push({ date, inCurrentMonth: false });
    }
  }

  // Zu Wochen (Arrays mit 7 Tagen) gruppieren
  for (let i = 0; i < days.length; i += 7) {
    weeksArray.push(days.slice(i, i + 7));
  }

  return weeksArray;
});

/**
 * Alle Tage des aktuellen Monats als einfache Liste (für mobile Ansicht)
 */
const allDaysInMonth = computed(() => {
  const days = [];
  const year = selectedYear.value;
  const month = selectedMonth.value; // 1-12
  const lastDay = new Date(year, month, 0).getDate();

  for (let d = 1; d <= lastDay; d++) {
    days.push({ date: new Date(year, month - 1, d) });
  }
  return days;
});

/**
 * Kombiniert alle Eventtypen: Benutzerevents, Feiertage, Ferien
 */
const combinedEvents = computed(() => {
  return [...events.value, ...holidayEvents.value, ...ferienEvents];
});

/**
 * Berechnet Feiertage für das aktuelle Jahr
 */
const holidayEvents = computed(() => {
  const year = selectedYear.value;
  const hd = new Holidays("DE", "NW");
  const holidaysList = hd.getHolidays(year) || [];

  // Nur offizielle Feiertage übernehmen
  return holidaysList
    .filter((holiday) => holiday.type === "public")
    .map((holiday, index) => {
      const dateOnly = holiday.date.substring(0, 10);
      return {
        id: `holiday-${index}`,
        title: holiday.name,
        description: "Feiertag in NRW",
        start_date: dateOnly + "T00:00",
        end_date: dateOnly + "T23:59",
        color: "#0000ff",
        is_holiday: true,
        draggable: false, // Feiertage sind nicht verschiebbar
      };
    });
});

// ======== Neue Konstanten für Eventtypen ========
const eventTypes = [
  { text: "Termin", value: "appointment" },
  { text: "Urlaub", value: "vacation" },
  { text: "Krankheit", value: "sick" },
  { text: "Geburtstag", value: "birthday" },
];

// ======== Berechnte Eigenschaften ========
// Prüft, ob der Event gespeichert werden kann
const isEventValid = computed(() => {
  // Bei regulären Terminen muss ein Titel existieren
  if (eventItem.value.event_type === "appointment") {
    return !!eventItem.value.title && eventItem.value.title.trim() !== "";
  }

  // Urlaub und Krankheit sind auch ohne Titel speicherbar
  return true;
});

// ======== Event-Handling Objekt ========
const eventItem = ref({
  title: "",
  description: "",
  start_date: formatDateTime(new Date()),
  end_date: formatDateTime(new Date(new Date().setHours(23, 59, 0, 0))),
  is_global: false,
  color: "#4CAF50", // Standardfarbe: Grün
  event_type: "appointment", // Standard: normaler Termin
});

// ======== Hilfsfunktionen für Eventtypen ========
/**
 * Gibt das passende Icon für den Eventtyp zurück
 */
function getEventTypeIcon(type) {
  switch (type) {
    case "vacation":
      return "mdi-beach";
    case "sick":
      return "mdi-medical-bag";
    case "birthday":
      return "mdi-cake-variant";
    default:
      return "mdi-calendar-plus";
  }
}

/**
 * Gibt die passende Farbe für den Eventtyp zurück
 */
function getEventTypeColor(type) {
  switch (type) {
    case "vacation":
      return "#FF9800"; // Orange
    case "sick":
      return "#F44336"; // Rot
    case "birthday":
      return "#E91E63"; // Pink
    default:
      return "#1976D2"; // Blau
  }
}

/**
 * Gibt den Platzhaltertext für das Titelfeld basierend auf dem Eventtyp zurück
 */
function getEventTypePlaceholder(type) {
  switch (type) {
    case "vacation":
      return "Grund für Urlaub (optional)";
    case "sick":
      return "Anmerkung zur Krankheit (optional)";
    case "birthday":
      return "Geburtstagsnotiz (optional)";
    default:
      return "Terminname eingeben";
  }
}

/**
 * Gibt den Text für die Checkbox basierend auf dem Eventtyp zurück
 */
function getGlobalCheckboxLabel(type) {
  switch (type) {
    case "vacation":
      return "Urlaubstage für alle sichtbar machen";
    case "sick":
      return "Krankmeldung für alle sichtbar machen";
    case "birthday":
      return "Geburtstag für alle sichtbar machen";
    default:
      return "Global sichtbar für alle Benutzer";
  }
}

/**
 * Wird aufgerufen, wenn der Eventtyp geändert wird
 */
function onEventTypeChange(newType) {
  // Setze standardmäßige Farben basierend auf dem Eventtyp
  switch (newType) {
    case "vacation":
      eventItem.value.color = "#FF9800"; // Orange für Urlaub
      // Optional: Standardtitel für Urlaub
      if (!eventItem.value.title || eventItem.value.title === "Termin") {
        eventItem.value.title = "Urlaub";
      }
      break;
    case "sick":
      eventItem.value.color = "#F44336"; // Rot für Krankheit
      // Optional: Standardtitel für Krankheit
      if (!eventItem.value.title || eventItem.value.title === "Termin") {
        eventItem.value.title = "Krankheit";
      }
      break;
    case "birthday":
      eventItem.value.color = "#E91E63"; // Pink für Geburtstage
      // Optional: Standardtitel für Geburtstag
      if (!eventItem.value.title || eventItem.value.title === "Termin") {
        eventItem.value.title = authStore.user ? `${authStore.user.name} hat Geburtstag` : "Geburtstag";
      }
      break;
    default:
      eventItem.value.color = "#4CAF50"; // Grün für normale Termine
      break;
  }
}

// ======== Hilfsfunktionen ========
/**
 * Robustere Formatierung eines Datums zu "YYYY-MM-DDTHH:mm"
 */
function formatDateTime(dateObj) {
  // pad außerhalb deklarieren, damit es im gesamten Funktionsscope verfügbar ist
  const pad = (v) => (v < 10 ? "0" + v : v);

  try {
    if (!dateObj || isNaN(dateObj.getTime())) {
      // Fallback auf das aktuelle Datum, wenn ungültig
      dateObj = new Date();
    }

    return (
      dateObj.getFullYear() +
      "-" +
      pad(dateObj.getMonth() + 1) +
      "-" +
      pad(dateObj.getDate()) +
      "T" +
      pad(dateObj.getHours()) +
      ":" +
      pad(dateObj.getMinutes())
    );
  } catch (e) {
    console.error("Fehler beim Formatieren des Datums:", e);

    // Notfall-Fallback: aktuelles Datum
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}T00:00`;
  }
}

/**
 * Berechnet die ISO-Kalenderwoche für ein Datum
 */
function getWeekNumber(d) {
  if (!d || d.getTime() === 0) return "";

  const date = new Date(d.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));

  const week1 = new Date(date.getFullYear(), 0, 4);
  const dayDiff = (week1.getDay() + 6) % 7;
  week1.setDate(week1.getDate() - dayDiff);

  const weekNumber = Math.round(((date - week1) / 86400000 + 1) / 7);
  return weekNumber;
}

/**
 * Formatiert Zeit aus einem Datumstring zu "HH:mm"
 */
function formatTime(dateStr) {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  const pad = (v) => (v < 10 ? "0" + v : v);
  return pad(dt.getHours()) + ":" + pad(dt.getMinutes());
}

/**
 * Prüft, ob ein Datum heute ist
 */
function isToday(date) {
  if (!date || date.getTime() === 0) return false;

  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Prüft, ob ein Tag ein Wochenende ist
 */
function isDayWeekend(date) {
  if (!date || date.getTime() === 0) return false;
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sonntag, 6 = Samstag
}

/**
 * Gibt den Wochentagnamen für ein Datum zurück
 */
function getDayName(date) {
  if (!date) return "";
  const dayNames = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return dayNames[date.getDay()];
}

/**
 * Ermittelt die Hintergrundfarbe für ein Event basierend auf Typ und Farbe
 */
function getEventBackground(event) {
  if (!event) return "rgba(66, 66, 66, 0.8)";

  // Feiertage haben blauen Hintergrund
  if (isEventType(event, "holiday")) {
    return "rgba(0, 0, 255, 0.2)";
  }

  // Ferien haben hellblauen Hintergrund
  if (isEventType(event, "ferien")) {
    return "rgba(33, 150, 243, 0.2)";
  }

  // Neue Eventtypen
  if (event.event_type === "vacation") {
    return "rgba(255, 152, 0, 0.2)"; // Orange mit Transparenz für Urlaub
  }

  if (event.event_type === "sick") {
    return "rgba(244, 67, 54, 0.2)"; // Rot mit Transparenz für Krankheit
  }

  if (event.event_type === "birthday") {
    return "rgba(233, 30, 99, 0.2)"; // Pink mit Transparenz für Geburtstage
  }

  // Benutzerevents haben eine angepasste Farbe mit Transparenz
  const color = event.color || "#4CAF50";
  return colorToRgba(color, 0.2);
}

/**
 * Konvertiert eine Hex-Farbe zu RGBA mit Transparenz
 */
function colorToRgba(hex, alpha = 1) {
  if (!hex) return `rgba(66, 66, 66, ${alpha})`;

  // Hex zu RGB konvertieren
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Filtert Termine für einen bestimmten Tag
 */
function eventsOnDate(date) {
  if (!date || date.getTime() === 0) return [];

  return combinedEvents.value.filter((event) => {
    const start = new Date(event.start_date);
    const end = new Date(event.end_date);
    const checkDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const startDate = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate()
    );
    const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    return checkDate >= startDate && checkDate <= endDate;
  });
}

/**
 * Liefert alle Events eines Tages als Array für Drag & Drop
 */
function getDraggableEvents(date) {
  if (!date || date.getTime() === 0) return [];

  return eventsOnDate(date).map((event) => {
    // Feiertage und Ferien sind nicht verschiebbar
    const isDraggable =
      !isEventType(event, "holiday") &&
      !isEventType(event, "ferien") &&
      event.user_id === authStore.user?.id;

    return {
      ...event,
      draggable: isDraggable,
    };
  });
}

/**
 * Neue Hilfsfunktion zur sicheren Event-Typ-Prüfung
 */
function isEventType(event, type) {
  return event?.id && typeof event.id === "string" && event.id.startsWith(type);
}

// ======== Navigation & Actions ========
/**
 * Zum vorherigen Monat wechseln
 */
function prevMonth() {
  if (selectedMonth.value === 1) {
    selectedMonth.value = 12;
    selectedYear.value--;
  } else {
    selectedMonth.value--;
  }
}

/**
 * Zum nächsten Monat wechseln
 */
function nextMonth() {
  if (selectedMonth.value === 12) {
    selectedMonth.value = 1;
    selectedYear.value++;
  } else {
    selectedMonth.value++;
  }
}

/**
 * Zum aktuellen Monat/Jahr springen
 */
function goToToday() {
  const today = new Date();
  selectedMonth.value = today.getMonth() + 1;
  selectedYear.value = today.getFullYear();
}

/**
 * Dialog für neuen Event öffnen
 */
function openAddEventDialog(date = null) {
  eventEditMode.value = false;

  // Start- und Enddatum definieren
  let startDate, endDate;

  try {
    // Strikte Prüfung des übergebenen Datums
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      // Wenn ein gültiges Datum angeklickt wurde, dieses verwenden
      startDate = new Date(date);
      endDate = new Date(date);
    } else {
      // Bei null, undefined oder ungültigem Datum: heutiges Datum verwenden
      startDate = new Date();
      endDate = new Date();
    }

    // Startzeit auf 00:00 Uhr setzen
    startDate.setHours(0, 0, 0, 0);

    // Endzeit auf 23:59 Uhr setzen
    endDate.setHours(23, 59, 0, 0);

    const formattedStart = formatDateTime(startDate);
    const formattedEnd = formatDateTime(endDate);

    // Dialog öffnen mit gültigen Werten
    eventItem.value = {
      title: "",
      description: "",
      start_date: formattedStart,
      end_date: formattedEnd,
      is_global: false,
      color: "#4CAF50",
      event_type: "appointment", // Standard: normaler Termin
    };
  } catch (error) {
    // Bei Fehlern Standardwerte verwenden
    console.error("Fehler beim Initialisieren des Ereignisses:", error);

    const today = new Date();
    const todayEnd = new Date();
    today.setHours(0, 0, 0, 0);
    todayEnd.setHours(23, 59, 0, 0);

    eventItem.value = {
      title: "",
      description: "",
      start_date: formatDateTime(today),
      end_date: formatDateTime(todayEnd),
      is_global: false,
      color: "#4CAF50",
    };
  }

  // Dialog erst öffnen, wenn die Werte gesetzt sind
  nextTick(() => {
    eventDialog.value = true;
  });
}

/**
 * Validiert und korrigiert Datum/Zeit-Eingaben mit Berücksichtigung der Reihenfolge
 */
function validateDateTimeField(field) {
  // Aktuellen Wert sichern
  const value = eventItem.value[field];

  // Wenn leer, Standardwert setzen
  if (!value || value.trim() === "") {
    const now = new Date();
    if (field === "start_date") {
      now.setHours(0, 0, 0, 0);
    } else {
      now.setHours(23, 59, 0, 0);
    }
    eventItem.value[field] = formatDateTime(now);
    return;
  }

  // Wenn nur ein Datum eingegeben wurde (ohne Uhrzeit)
  if (!value.includes("T")) {
    try {
      // ISO-Datumsformat wie "2025-03-15"
      const dateParts = value.split("-");
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1;
        const day = parseInt(dateParts[2]);

        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          const date = new Date(year, month, day);

          if (field === "start_date") {
            date.setHours(0, 0, 0, 0);

            // Überprüfen, ob das neue Startdatum nach dem Enddatum liegt
            const endDate = new Date(eventItem.value.end_date);
            if (!isNaN(endDate.getTime()) && date > endDate) {
              // Ende auf gleichen Tag mit Endzeit 23:59 setzen
              const newEndDate = new Date(date);
              newEndDate.setHours(23, 59, 0, 0);
              eventItem.value.end_date = formatDateTime(newEndDate);
            }
          } else {
            date.setHours(23, 59, 0, 0);

            // Überprüfen, ob das neue Enddatum vor dem Startdatum liegt
            const startDate = new Date(eventItem.value.start_date);
            if (!isNaN(startDate.getTime()) && date < startDate) {
              // Enddatum auf Startdatum mit Endzeit 23:59 setzen
              date.setFullYear(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate()
              );
              date.setHours(23, 59, 0, 0);
            }
          }

          // Wert aktualisieren
          eventItem.value[field] = formatDateTime(date);
          return;
        }
      }

      throw new Error("Ungültiges Datumsformat");
    } catch (e) {
      console.error("Fehler beim Validieren des Datums:", e);
      // Bei Fehler das originale Eingabefeld behalten
      return;
    }
  } else {
    // Falls vollständiges Datum mit Uhrzeit eingegeben wurde
    // Prüfe Reihenfolge von Start- und Enddatum
    try {
      if (field === "start_date") {
        const startDate = new Date(value);
        const endDate = new Date(eventItem.value.end_date);

        if (
          !isNaN(startDate.getTime()) &&
          !isNaN(endDate.getTime()) &&
          startDate > endDate
        ) {
          // Das neue Startdatum liegt nach dem Enddatum, passe das Enddatum an
          const newEndDate = new Date(startDate);
          newEndDate.setHours(23, 59, 0, 0);
          eventItem.value.end_date = formatDateTime(newEndDate);
        }
      } else if (field === "end_date") {
        const endDate = new Date(value);
        const startDate = new Date(eventItem.value.start_date);

        if (
          !isNaN(startDate.getTime()) &&
          !isNaN(endDate.getTime()) &&
          endDate < startDate
        ) {
          // Das neue Enddatum liegt vor dem Startdatum, setze es auf das Startdatum + 23:59
          endDate.setFullYear(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
          );
          endDate.setHours(23, 59, 0, 0);
          eventItem.value[field] = formatDateTime(endDate);
        }
      }
    } catch (e) {
      console.error("Fehler bei der Datumsvalidierung:", e);
    }
  }
}

/**
 * Dialog zum Bearbeiten eines Events öffnen
 */
function openEditEventDialog(event) {
  if (!event || !authStore.user) return;

  // Nur eigene Events bearbeiten (außer für Admins)
  if (
    event.user_id !== authStore.user.id &&
    !authStore.user.admin &&
    !authStore.user.super_admin &&
    !authStore.user.developer
  ) {
    toast.info("Du kannst nur deine eigenen Termine bearbeiten.");
    return;
  }

  eventEditMode.value = true;
  eventItem.value = {
    ...event,
    is_global: Boolean(event.is_global),
    event_type: event.event_type || "appointment",
  };
  eventDialog.value = true;
}

/**
 * Event speichern (neu anlegen oder aktualisieren)
 */
async function saveEvent() {
  // Validiere Event-Daten
  if (!eventItem.value.title || !eventItem.value.title.trim()) {
    toast.error("Bitte gib einen Titel ein.");
    return;
  }

  // Parsen der Datumswerte
  const start = new Date(eventItem.value.start_date);
  const end = new Date(eventItem.value.end_date);

  // Validierung: Enddatum darf nicht vor dem Startdatum liegen
  if (end < start) {
    toast.error("Das Enddatum darf nicht vor dem Startdatum liegen.");
    return;
  }

  loading.value = true;

  try {
    // Bestimme die Aktion: "add" oder "update"
    const action = eventEditMode.value ? "update" : "add";

    const response = await axios.post(
      `${API_BASE_URL}calendar.php`,
      {
        ...eventItem.value,
        action,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        eventEditMode.value
          ? "Termin wurde erfolgreich aktualisiert!"
          : "Termin wurde erfolgreich angelegt!"
      );
      eventDialog.value = false;
      fetchEvents();
    } else {
      toast.error(response.data.message || "Ein Fehler ist aufgetreten.");
    }
  } catch (error) {
    console.error("Fehler beim Speichern des Events:", error);
    toast.error(
      "Der Termin konnte nicht gespeichert werden. Bitte versuche es später erneut."
    );
  } finally {
    loading.value = false;
  }
}

/**
 * Löschen bestätigen und Event löschen
 */
async function deleteEventConfirmed() {
  if (!eventItem.value.id) {
    toast.error("Ungültige Event-ID.");
    return;
  }

  loading.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}calendar.php`,
      {
        id: eventItem.value.id,
        action: "delete",
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Termin wurde erfolgreich gelöscht!");
      confirmDelete.value = false;
      eventDialog.value = false;
      fetchEvents();
    } else {
      toast.error(response.data.message || "Ein Fehler ist aufgetreten.");
    }
  } catch (error) {
    console.error("Fehler beim Löschen des Events:", error);
    toast.error(
      "Der Termin konnte nicht gelöscht werden. Bitte versuche es später erneut."
    );
  } finally {
    loading.value = false;
  }
}

/**
 * Wird ausgeführt, wenn ein Event verschoben wurde.
 */
function onEventChanged(targetDate, changeEvent) {
  if (changeEvent.added && changeEvent.added.element) {
    const evt = changeEvent.added.element;

    // Ignorieren, wenn Event nicht verschiebbar ist
    if (isEventType(evt, "holiday") || isEventType(evt, "ferien")) {
      toast.info("Feiertage und Ferien können nicht verschoben werden.");
      fetchEvents(); // Zurücksetzen
      return;
    }

    // Ignorieren, wenn User nicht der Besitzer ist
    if (evt.user_id !== authStore.user?.id && !authStore.user?.admin) {
      toast.info("Du kannst nur deine eigenen Termine verschieben.");
      fetchEvents(); // Zurücksetzen
      return;
    }

    // Berechne neue Start- und End-Daten:
    const originalStart = new Date(evt.start_date);
    const originalEnd = new Date(evt.end_date);
    const duration = originalEnd - originalStart;

    const newStart = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      originalStart.getHours(),
      originalStart.getMinutes()
    );

    const newEnd = new Date(newStart.getTime() + duration);

    evt.start_date = formatDateTime(newStart);
    evt.end_date = formatDateTime(newEnd);

    toast.info("Termin wird verschoben...");

    // API-Aufruf zum Persistieren:
    axios
      .post(
        `${API_BASE_URL}calendar.php`,
        { ...evt, action: "update" },
        { withCredentials: true }
      )
      .then((response) => {
        if (response.data.status === "success") {
          toast.success("Termin wurde erfolgreich verschoben!");
        } else {
          toast.error(response.data.message || "Ein Fehler ist aufgetreten.");
        }
        fetchEvents();
      })
      .catch((error) => {
        console.error("Fehler beim Aktualisieren des Events:", error);
        toast.error("Der Termin konnte nicht verschoben werden.");
        fetchEvents();
      });
  }
}

// ======== API-Zugriff ========
/**
 * Events für den aktuellen Monat/Jahr vom Server laden
 */
async function fetchEvents() {
  loading.value = true;

  try {
    const response = await axios.get(`${API_BASE_URL}calendar.php`, {
      params: {
        month: selectedMonth.value,
        year: selectedYear.value,
      },
      withCredentials: true,
    });

    if (response.data.status === "success") {
      events.value = response.data.events;
    } else {
      console.error("Fehler beim Abrufen der Termine:", response.data.message);
      toast.error("Die Termine konnten nicht geladen werden.");
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Termine:", error);
    toast.error("Verbindungsfehler beim Laden der Termine.");
  } finally {
    loading.value = false;
  }
}

function formatMitarbeiternummer(mitarbeiternummer) {
  if (!mitarbeiternummer) return "";
  // Wenn die Mitarbeiternummer zu lang ist, kürzen
  if (mitarbeiternummer.length > 5) {
    return mitarbeiternummer.substring(0, 5) + "...";
  }
  return mitarbeiternummer;
}

/**
 * Prüft, ob die Mitarbeiternummer angezeigt werden soll
 */
function shouldShowMitarbeiternummer(event) {
  // Nicht für eigene Events, Feiertage oder Ferien anzeigen
  if (isEventType(event, "holiday") ||
      isEventType(event, "ferien") ||
      (authStore.user && event.user_id === authStore.user.id)) {
    return false;
  }

  return event.mitarbeiternummer && event.mitarbeiternummer.trim() !== "";
}

/**
 * Prüft, ob Ersteller-Informationen im Tooltip angezeigt werden sollen
 */
function shouldShowCreatorInfo(event) {
  // Nicht für Feiertage oder Ferien anzeigen
  if (isEventType(event, "holiday") || isEventType(event, "ferien")) {
    return false;
  }

  return event.mitarbeiternummer && event.mitarbeiternummer.trim() !== "";
}

/**
 * Gibt Benutzerinformationen für den Tooltip zurück
 */
function getEventCreatorInfo(event) {
  if (!event.mitarbeiternummer) return "";
  return `Erstellt von: ${event.mitarbeiternummer}`;
}

// ======== Fenstergröße ========
function updateWidth() {
  windowWidth.value = window.innerWidth;
}

// ======== Lebenszyklus-Hooks ========
onMounted(() => {
  fetchEvents();
  window.addEventListener("resize", updateWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
});

// Reagiere auf Änderungen von Monat oder Jahr
watch([selectedMonth, selectedYear], () => {
  fetchEvents();
});
watch(
  () => eventItem.value.start_date,
  (newStartDate) => {
    try {
      const startDate = new Date(newStartDate);
      const endDate = new Date(eventItem.value.end_date);

      // Wenn das Startdatum nach dem Enddatum liegt oder das Format ungültig ist
      if (isNaN(endDate.getTime()) || startDate > endDate) {
        // Setze das Enddatum auf den gleichen Tag wie das Startdatum, aber mit Endzeit 23:59
        const newEndDate = new Date(startDate);
        newEndDate.setHours(23, 59, 0, 0);
        eventItem.value.end_date = formatDateTime(newEndDate);

        // Optional: Zeige einen Hinweis an
        toast.info("Das Enddatum wurde automatisch angepasst.");
      }
    } catch (e) {
      console.error("Fehler bei der Datumsvalidierung:", e);
    }
  }
);

// Manuell definierte Ferientage für NRW 2025 und 2026
const ferienEvents = [
  // 2025
  {
    id: "ferien-2025-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2025",
    start_date: "2025-04-14T00:00",
    end_date: "2025-04-26T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2025-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2025",
    start_date: "2025-07-16T00:00",
    end_date: "2025-08-28T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2025-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2025",
    start_date: "2025-10-13T00:00",
    end_date: "2025-10-25T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2025-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2025",
    start_date: "2025-12-23T00:00",
    end_date: "2026-01-06T23:59",
    color: "#2196F3",
  },
  // 2026
  {
    id: "ferien-2026-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2026",
    start_date: "2026-03-29T00:00",
    end_date: "2026-04-09T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2026-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2026",
    start_date: "2026-07-15T00:00",
    end_date: "2026-08-27T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2026-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2026",
    start_date: "2026-10-12T00:00",
    end_date: "2026-10-24T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2026-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2026",
    start_date: "2026-12-23T00:00",
    end_date: "2027-01-06T23:59",
    color: "#2196F3",
  },
  // 2027
  {
    id: "ferien-2027-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2027",
    start_date: "2027-04-03T00:00",
    end_date: "2027-04-15T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2027-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2027",
    start_date: "2027-07-14T00:00",
    end_date: "2027-08-26T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2027-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2027",
    start_date: "2027-10-11T00:00",
    end_date: "2027-10-23T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2027-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2027",
    start_date: "2027-12-23T00:00",
    end_date: "2028-01-06T23:59",
    color: "#2196F3",
  },
  // 2028
  {
    id: "ferien-2028-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2028",
    start_date: "2028-03-25T00:00",
    end_date: "2028-04-06T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2028-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2028",
    start_date: "2028-07-12T00:00",
    end_date: "2028-08-24T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2028-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2028",
    start_date: "2028-10-09T00:00",
    end_date: "2028-10-21T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2028-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2028",
    start_date: "2028-12-23T00:00",
    end_date: "2029-01-06T23:59",
    color: "#2196F3",
  },
];

/**
 * Provide Injektionen für das Calendar-Widget
 */
provide("displayedFeiertageEvents", computed(() => {
  return combinedEvents.value.filter(
    (event) => isEventType(event, "holiday") || isEventType(event, "ferien")
  );
}));

provide("upcomingEvents", computed(() => {
  const now = new Date();
  const oneWeekLater = new Date(now);
  oneWeekLater.setDate(oneWeekLater.getDate() + 7);

  return combinedEvents.value.filter((event) => {
    const endDate = new Date(event.end_date);
    return endDate >= now;
  });
}));
</script>

<style scoped>
/* ======== Container & Basis-Layout ======== */
.calendar-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

/* ======== Karten & Container ======== */
.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

/* ======== Tab-Navigation ======== */
.calendar-tabs {
  background: transparent;
}

.calendar-tabs :deep(.v-tab) {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}

.calendar-tabs :deep(.v-tab--selected) {
  background: rgba(76, 175, 80, 0.1);
}

.calendar-tabs :deep(.v-tab:hover) {
  background: rgba(76, 175, 80, 0.05);
}

.card-divider {
  opacity: 0.1;
  margin: 0;
}

.dialog-card {
  background-color: rgba(30, 30, 35, 0.9);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.dialog-title {
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 500;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dialog-divider {
  opacity: 0.1;
}

.dialog-content {
  padding: 20px;
}

.dialog-actions {
  padding: 12px 16px;
}

.warning-header {
  background: rgba(var(--v-theme-error), 0.1);
}

/* ======== Formulare & Eingabefelder ======== */
.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

/* ======== Kalender Header ======== */
.calendar-actions-container {
  padding-top: 0 !important;
  padding-bottom: 12px !important;
}

.calendar-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.month-navigation {
  display: flex;
  align-items: center;
  gap: 10px;
}

.month-title {
  font-size: 1.2rem;
  font-weight: 600;
  white-space: nowrap;
  margin: 0;
  min-width: 150px;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.5px;
}

.nav-btn {
  border-radius: 8px !important;
  min-width: 36px;
  min-height: 36px;
  transition: transform 0.2s, background-color 0.2s;
}

.nav-btn:hover {
  transform: translateY(-2px);
  background-color: rgba(76, 175, 80, 0.15) !important;
}

.today-btn {
  border-radius: 8px !important;
}

.date-selector {
  border-radius: 8px !important;
  white-space: nowrap;
}

.add-event-btn {
  border-radius: 8px !important;
  box-shadow: 0 3px 8px rgba(76, 175, 80, 0.3) !important;
  background-color: var(--v-theme-success) !important;
}

.add-event-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

/* ======== Datumsauswahl Dialog ======== */
.date-picker-card {
  background: rgba(35, 35, 45, 0.95) !important;
}

.date-picker-container {
  display: flex;
  gap: 16px;
}

.month-select,
.year-select {
  min-width: 130px;
}

/* ======== Desktop Kalender Layout ======== */
.desktop-calendar {
  max-height: 75vh;
  overflow-y: auto;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  padding: 0 4px 16px; /* Reduzierter Seitenabstand */
}

.desktop-calendar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.desktop-calendar::-webkit-scrollbar-track {
  background: transparent;
}

.desktop-calendar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.desktop-calendar::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

.week-days-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: rgba(20, 20, 30, 0.8);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 6px; /* Reduzierter Abstand */
  position: sticky;
  top: 0;
  z-index: 3;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.week-day-label {
  padding: 10px 4px;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 1px;
}

/* Wochenenden hervorheben */
.week-days-header .week-day-label:nth-child(6),
.week-days-header .week-day-label:nth-child(7) {
  color: rgba(255, 152, 0, 0.9);
}

.calendar-grid {
  display: flex;
  flex-direction: column;
  gap: 5px; /* Reduzierter vertikaler Abstand */
}

.calendar-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px; /* Reduzierter horizontaler Abstand */
}

/* ======== Tageszellen ======== */
.calendar-day {
  background: rgba(35, 35, 45, 0.4);
  border-radius: 10px; /* Kleinerer Radius */
  border: 1px solid rgba(255, 255, 255, 0.05);
  height: 120px; /* Feste Höhe */
  min-width: 0; /* Verhindert Überlaufen */
  padding: 6px 4px 4px; /* Weniger Padding */
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Verhindert Überlaufen */
}

.calendar-day:hover {
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(40, 40, 50, 0.5);
}

.today {
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.non-current-month {
  opacity: 0.5;
}

.weekend {
  background: rgba(40, 35, 50, 0.4);
}

.has-events {
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.15);
}

.week-number {
  position: absolute;
  top: 3px;
  left: 3px;
  background: rgba(30, 30, 40, 0.7);
  font-size: 0.7rem;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  letter-spacing: -0.2px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.week-number::before {
  content: "KW";
  font-size: 0.65rem;
  opacity: 0.7;
  margin-right: 3px;
}

.day-number {
  text-align: right;
  margin-bottom: 6px; /* Weniger Abstand */
  position: relative;
  z-index: 2;
}

.date-container {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  display: inline-block;
  padding: 1px 6px;
  border-radius: 10px;
}

.today-marker {
  background: rgba(76, 175, 80, 0.8);
  color: white;
  font-weight: 700;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* ======== Terminelemente ======== */
.day-events {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 3px; /* Kleinerer Abstand zwischen Events */
  padding-top: 3px;
  overflow-y: auto;
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 5%,
    black 95%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 5%,
    black 95%,
    transparent 100%
  );
  max-height: 84px; /* Mehr Platz für Events */
}

.event-item {
  background: rgba(50, 50, 60, 0.8);
  border-radius: 4px;
  border-left: 3px solid #fff;
  padding: 2px 6px;
  position: relative;
  cursor: grab;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 20px; /* Noch kleinere Höhe */
  font-size: 0.8rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.event-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
}

.event-content {
  flex-grow: 1;
  min-width: 0; /* Für Text-Overflow */
  display: flex;
  gap: 3px;
  align-items: center;
}

.event-time {
  font-size: 0.65rem; /* Kleinere Schrift */
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

.event-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  font-size: 0.7rem; /* Noch kleinere Schrift */
}

.event-tooltip {
  max-width: 250px;
  font-size: 0.8rem;
}

.event-edit-btn {
  opacity: 0;
  transition: opacity 0.2s;
  background: rgba(0, 0, 0, 0.3) !important;
  border-radius: 50% !important;
  height: 18px !important; /* Noch kleinerer Button */
  width: 18px !important;
  min-width: 18px !important;
  margin: -2px;
}

.event-item:hover .event-edit-btn {
  opacity: 1;
}

/* Spezielle Events */
.holiday-event {
  background: rgba(0, 0, 200, 0.15);
  border-left: 3px solid #0000ff;
  border-radius: 4px;
}

.ferien-event {
  background: rgba(33, 150, 243, 0.15);
  border-left: 3px solid #2196f3;
  border-radius: 4px;
}

.user-event {
  border-left-width: 3px;
}

/* Neue Event-Typen */
.vacation-event {
  background: rgba(255, 152, 0, 0.15) !important;
  border-left: 3px solid #ff9800 !important;
  border-radius: 4px;
}

.sick-event {
  background: rgba(244, 67, 54, 0.15) !important;
  border-left: 3px solid #f44336 !important;
  border-radius: 4px;
}

.birthday-event {
  background: rgba(233, 30, 99, 0.15) !important;
  border-left: 3px solid #e91e63 !important;
  border-radius: 4px;
}

/* ======== Verbessertes Design für eigene Events ======== */

/* Verbesserte Unterscheidung zwischen eigenen und fremden Events */
.owned-event {
  background: linear-gradient(
    95deg,
    rgba(255, 255, 255, 0.07),
    rgba(255, 255, 255, 0.03)
  );
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  border-left-width: 3px;
  position: relative;
  overflow: hidden;
}

.owned-event::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 8px;
  background: linear-gradient(
    135deg,
    transparent 50%,
    rgba(255, 255, 255, 0.3) 50%
  );
}

.owned-event .event-time {
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
}

/* ======== Mobile Agenda-Ansicht ======== */
.mobile-calendar {
  padding: 0 4px 16px;
}

.mobile-day-list {
  display: flex;
  flex-direction: column;
  gap: 10px; /* Reduzierter Abstand */
}

.mobile-day-item {
  background: rgba(35, 35, 45, 0.5);
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mobile-day-item.today {
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  box-shadow: 0 2px 10px rgba(76, 175, 80, 0.15);
}

.mobile-day-item.weekend {
  background: rgba(40, 35, 50, 0.5);
}

.mobile-day-header {
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(25, 25, 35, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.mobile-date-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-day-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  min-width: 30px;
}

.mobile-day-number {
  font-size: 1.1rem;
  font-weight: 700;
}

.today .mobile-day-number {
  color: var(--v-theme-success);
}

.mobile-week-number {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  background: rgba(30, 30, 40, 0.6);
  padding: 2px 6px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-week-number::before {
  content: "KW";
  font-size: 0.7rem;
  opacity: 0.7;
  margin-right: 3px;
}

.mobile-day-events {
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mobile-event-item {
  padding: 8px 10px;
  border-radius: 6px;
  border-left: 3px solid #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.mobile-event-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.mobile-event-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-grow: 1;
}

.mobile-event-time {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

.mobile-event-title {
  font-weight: 500;
  font-size: 0.9rem;
}

.mobile-event-edit-btn {
  background: rgba(0, 0, 0, 0.3) !important;
  border-radius: 50% !important;
  height: 24px !important;
  width: 24px !important;
}

.mobile-event-item.owned-event {
  background: linear-gradient(
    95deg,
    rgba(255, 255, 255, 0.07),
    rgba(255, 255, 255, 0.03)
  );
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  border-left-width: 3px;
  position: relative;
  overflow: hidden;
}

.mobile-event-item.owned-event::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 10px;
  height: 10px;
  background: linear-gradient(
    135deg,
    transparent 50%,
    rgba(255, 255, 255, 0.3) 50%
  );
}

.no-events-message {
  padding: 10px 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  font-size: 0.9rem;
}

/* ======== Farbauswahl im Dialog ======== */
.color-selection-container {
  margin-top: 20px;
}

.color-selection-label {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-weight: 500;
}

.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.color-option {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.color-option:hover {
  transform: scale(1.15);
}

.color-option.selected {
  border: 2px solid white;
  transform: scale(1.2);
}

/* ======== Datum und Zeit-Felder ======== */
.date-time-fields-wrapper {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 14px;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin-left: 4px;
}

.full-width-datetime {
  width: 100%;
}

.full-width-datetime :deep(input) {
  width: 100% !important;
  font-family: "Roboto Mono", monospace;
  letter-spacing: -0.5px;
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 1200px) {
  .calendar-day {
    height: 110px;
  }

  .day-events {
    max-height: 74px;
  }

  .calendar-grid {
    gap: 4px;
  }

  .calendar-week {
    gap: 4px;
  }
}

@media (max-width: 959px) {
  .calendar-actions {
    justify-content: space-between;
  }

  .calendar-day {
    height: 100px;
  }

  .day-events {
    max-height: 64px;
  }

  .calendar-grid {
    gap: 3px;
  }

  .calendar-week {
    gap: 3px;
  }
}

@media (max-width: 768px) {
  .month-title {
    font-size: 1.1rem;
    min-width: 120px;
  }

  .calendar-day {
    height: 90px;
  }

  .day-events {
    max-height: 54px;
  }

  .event-item {
    min-height: 18px;
  }

  .event-title,
  .event-time {
    font-size: 0.65rem;
  }

  .calendar-grid {
    gap: 2px;
  }

  .calendar-week {
    gap: 2px;
  }
}

@media (max-width: 600px) {
  .month-title {
    font-size: 1rem;
    min-width: 100px;
  }

  .week-day-label {
    padding: 6px 2px;
    font-size: 0.75rem;
  }

  .today-btn,
  .date-selector {
    font-size: 0.8rem;
  }
}

/* Neue Stile für Benutzer-Badge */
.user-badge {
  display: inline-block;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 0 3px;
  margin-left: 3px;
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: normal;
  vertical-align: middle;
  letter-spacing: -0.3px;
}

.event-tooltip-content {
  font-size: 0.8rem;
  max-width: 250px;
}

.tooltip-creator-info {
  margin-top: 5px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  gap: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 4px;
}

/* Verbesserte Unterscheidung zwischen eigenen und fremden Events */
.owned-event {
  background: linear-gradient(
    95deg,
    rgba(76, 175, 80, 0.1),
    rgba(76, 175, 80, 0.05)
  ) !important;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.user-event:not(.owned-event) {
  background: linear-gradient(
    95deg,
    rgba(30, 136, 229, 0.1),
    rgba(30, 136, 229, 0.05)
  ) !important;
  border: 1px solid rgba(30, 136, 229, 0.2);
}
</style>
